import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Stack,
  Tab,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import { useAdminReducer } from "../hooks/useAdminReducer";

// Common components
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import SearchControls from "../components/common/SearchControls";
import DataTable from "../components/common/DataTable";
import TablePagination from "../components/common/TablePagination";
import DeleteModal from "../ui-components/DeleteModal";

// API actions
import {
  updateAboutDirector,
  getAboutDirector,
  saveDirector,
  getDirectorOrderList,
  deleteDirector,
} from "../actions/about.actions";
import ToolBody from "../components/common/ToolBody";

// Styled components
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

const Directors = () => {
  const { storage } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("1");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [editDialog, setEditDialog] = useState({ open: false, item: null });

  // Replace useAdminReducer with useAdminPage
  const {
    state,
    setLoading,
    isLoading,
    handleFieldChange,
    handleFormError,
    handleImageChange,
    resetForm,
    setFormData,
    setSectionData,
    dispatch,
    setTableData,
    setTableLoading,
    setTableSearch,
    setTableSort,
  } = useAdminReducer({
    sectionData: {
      director_section_id: 0,
      section_title: "",
      section_title_error: false,
      section_title_maxlenth: 25,
      section_description: "",
      section_description_maxlenth: 185,
      section_description_error: false,
    },
    formData: {
      director_id: 0,
      director_name: "",
      director_name_error: false,
      director_name_maxlenth: 20,
      director_specialization: "",
      director_specialization_error: false,
      director_specialization_maxlenth: 20,
      director_short_desc: "",
      director_short_desc_error: false,
      director_short_desc_maxlenth: 130,
      director_facebook_link: "",
      director_facebook_link_error: false,
      director_twitter_link: "",
      director_twitter_link_error: false,
      director_pinterest_link: "",
      director_pinterest_link_error: false,
      director_instagram_link: "",
      director_instagram_link_error: false,
      director_image: "",
      director_image_name: "",
      director_image_error: false,
      director_image_mob: "",
      director_image_mob_name: "",
      director_image_mob_error: false,
    },
    tableData: {
      data: [],
      loading: true,
      pagination: {
        pageNo: 1,
        perPage: 50,
        totalCount: 0,
        totalPage: 1,
      },
      search: {
        text: "",
        type: "director_name",
      },
      sort: {
        field: null,
        direction: null,
      },
    },
    images: {
      deskImage: "",
      mobImage: "",
    },
  });

  // Destructure state for easier access
  const { sectionData, formData, tableData, images } = state;
  const { deskImage, mobImage } = images;

  // Specific image handlers
  const handleDeskImageChange = (event) =>
    handleImageChange(event, "director_image", "deskImage");
  const handleMobImageChange = (event) =>
    handleImageChange(event, "director_image_mob", "mobImage");

  const clearUpload = (imageField, previewField) => {
    dispatch({
      type: "SET_IMAGE",
      formName: "formData",
      imageField,
      file: null,
      fileName: "",
      previewField,
      previewUrl: "",
    });
  };

  // Table data fetching
  const fetchTableData = useCallback(
    (params = {}) => {
      setTableLoading(true);

      const requestParams = {
        pageNo: params.pageNo || tableData.pagination.pageNo,
        perPage: params.perPage || tableData.pagination.perPage,
        searchText:
          params.searchText !== undefined
            ? params.searchText
            : tableData.search.text,
        searchType: params.searchType || tableData.search.type,
      };

      // sort parameters if available
      if (params.sort || tableData.sort.direction) {
        requestParams.sort = params.sort || tableData.sort.direction;
      }

      if (params.sortField || tableData.sort.field) {
        requestParams.sortvalue = params.sortField || tableData.sort.field;
      }

      // Fetch data from API
      getDirectorOrderList(requestParams)
        .then((res) => {
          if (res.data) {
            setTableData({
              data: res.data.data || [],
              pagination: {
                pageNo: Number(requestParams.pageNo),
                perPage: Number(requestParams.perPage),
                totalCount: res.data.pagination?.totalCount || 0,
                totalPage: res.data.pagination?.totalPage || 1,
              },
            });
          }
        })
        .catch(() => {
          setTableLoading(false);
          setErrorMsg("Failed to load data");
        });
    },
    [
      setTableData,
      setTableLoading,
      tableData.sort,
      tableData.search,
      tableData.pagination,
    ],
  );

  // Submit director section
  const submitSection = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate required fields
    let hasError = false;

    if (!sectionData.section_title) {
      handleFormError("section_title", true, "sectionData");
      hasError = true;
    }

    if (!sectionData.section_description) {
      handleFormError("section_description", true, "sectionData");
      hasError = true;
    }

    if (hasError) {
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append("director_section_id", sectionData.director_section_id);
    formData.append("section_title", sectionData.section_title);
    formData.append("section_description", sectionData.section_description);

    // Submit to API
    updateAboutDirector(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg("Director Section Updated Successfully!");
          setTimeout(() => setSuccessMsg(""), 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Submit director form
  const submitDirector = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate required fields
    let hasError = false;
    const requiredFields = [
      "director_name",
      "director_specialization",
      "director_short_desc",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        handleFormError(field, true);
        hasError = true;
      }
    });

    if (hasError) {
      setLoading(false);
      return;
    }

    // Create form data
    const formDataObj = new FormData();
    formDataObj.append("director_id", formData.director_id);
    formDataObj.append("director_name", formData.director_name);
    formDataObj.append(
      "director_specialization",
      formData.director_specialization,
    );
    formDataObj.append("director_short_desc", formData.director_short_desc);
    formDataObj.append(
      "director_facebook_link",
      formData.director_facebook_link || "",
    );
    formDataObj.append(
      "director_twitter_link",
      formData.director_twitter_link || "",
    );
    formDataObj.append(
      "director_pinterest_link",
      formData.director_pinterest_link || "",
    );
    formDataObj.append(
      "director_instagram_link",
      formData.director_instagram_link || "",
    );
    formDataObj.append("director_image", formData.director_image);
    formDataObj.append("director_image_mob", formData.director_image_mob);

    // Submit to API
    const isNew = formData.director_id === 0;

    saveDirector(formDataObj)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg(
            isNew
              ? "Director Added Successfully!"
              : "Director Updated Successfully!",
          );

          // Reset form and refresh data
          resetForm();
          fetchTableData();

          // Close dialog if editing
          if (editDialog.open) {
            setEditDialog({ open: false, item: null });
          }

          // Switch to list tab after successful save
          setActiveTab("3");

          setTimeout(() => setSuccessMsg(""), 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Table handlers
  const handleSort = useCallback(
    (field, direction) => {
      setTableSort({ field, direction });
      fetchTableData({ pageNo: 1, sort: direction, sortField: field });
    },
    [setTableSort, fetchTableData],
  );

  const handleSearch = () => fetchTableData({ pageNo: 1 });

  const handleSearchTextChange = (text) => setTableSearch({ text });

  const handleSearchTypeChange = (type) => setTableSearch({ type });

  const clearSearch = useCallback(() => {
    setTableSearch({ text: "", type: "director_name" });
    fetchTableData({ pageNo: 1, searchText: "", searchType: "director_name" });
  }, [setTableSearch, fetchTableData]);

  const handlePageChange = (page) => fetchTableData({ pageNo: page });

  const handlePerPageChange = (perPage) =>
    fetchTableData({ pageNo: 1, perPage });

  // Edit director
  const handleEdit = useCallback(
    (item) => {
      // Reset form first
      resetForm();

      // Set form data from item
      setFormData({
        director_id: item.director_id,
        director_name: item.director_name || "",
        director_specialization: item.director_specialization || "",
        director_short_desc: item.director_short_desc || "",
        director_facebook_link: item.director_facebook_link || "",
        director_twitter_link: item.director_twitter_link || "",
        director_pinterest_link: item.director_pinterest_link || "",
        director_instagram_link: item.director_instagram_link || "",
        director_image_name: item.director_image || "",
        director_image_mob_name: item.director_image_mob || "",
      });

      // Set images if available
      if (item.director_image) {
        handleImageChange(
          { target: { files: null } },
          "director_image",
          "deskImage",
          item.director_image,
          pathJoinUrl(storage[0].imageEndUrl, item.director_image),
        );
      }

      if (item.director_image_mob) {
        handleImageChange(
          { target: { files: null } },
          "director_image_mob",
          "mobImage",
          item.director_image_mob,
          pathJoinUrl(storage[0].imageEndUrl, item.director_image_mob),
        );
      }

      setSuccessMsg("");
      setErrorMsg("");

      if (activeTab === "3") {
        // If in list view, open dialog
        setEditDialog({ open: true, item });
      } else {
        // If not in list view, switch to add/edit tab
        setActiveTab("2");
      }
    },
    [
      setEditDialog,
      setActiveTab,
      setFormData,
      handleImageChange,
      resetForm,
      storage,
      activeTab,
    ],
  );

  // Delete director
  const handleDelete = (item) => setDeleteDialog({ open: true, item });

  const confirmDelete = () => {
    setLoading(true);

    deleteDirector(deleteDialog.item.director_id)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg("Director Deleted Successfully!");
          fetchTableData();
          setDeleteDialog({ open: false, item: null });
          setTimeout(() => setSuccessMsg(""), 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Initialize data on component mount
  useEffect(() => {
    // Get director section data
    getAboutDirector().then((res) => {
      if (res.data.status === true) {
        setSectionData({
          director_section_id: res.data.data.director_section_id || 0,
          section_title: res.data.data.section_title || "",
          section_description: res.data.data.section_description || "",
        });
      }
    });

    // Load table data
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table column configuration
  const tableColumns = [
    {
      field: "index",
      label: "Sl No",
      width: "5%",
      render: (row, index) => {
        try {
          const pageNo = Number(tableData.pagination.pageNo) || 1;
          const perPage = Number(tableData.pagination.perPage) || 10;
          const rowNumber = (pageNo - 1) * perPage + (index + 1);
          return isNaN(rowNumber) ? "-" : String(rowNumber);
        } catch (error) {
          return "-";
        }
      },
    },
    {
      field: "director_name",
      label: "Name",
      width: "15%",
      sortable: true,
    },
    {
      field: "director_specialization",
      label: "Specialization",
      width: "15%",
    },
    {
      field: "director_short_desc",
      label: "Description",
      width: "20%",
    },
    {
      field: "director_image",
      label: "Image",
      width: "15%",
      render: (row) =>
        row.director_image && (
          <Box sx={{ width: 60, height: 40 }}>
            <img
              src={pathJoinUrl(storage[0].imageEndUrl, row.director_image)}
              alt="Thumbnail"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        ),
    },
    {
      field: "actions",
      label: "Actions",
      width: "15%",
      render: (row) => (
        <Stack direction="row" spacing={1}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: "pointer" }}
            onClick={() => handleEdit(row)}
          >
            <EditIcon fontSize="small" />
          </Typography>
          <Typography
            variant="body2"
            color="error"
            sx={{ cursor: "pointer" }}
            onClick={() => handleDelete(row)}
          >
            <DeleteIcon fontSize="small" />
          </Typography>
        </Stack>
      ),
    },
  ];

  const searchOptions = [
    { value: "director_name", label: "Name" },
    { value: "director_specialization", label: "Specialization" },
  ];

  // Render director form - extracted as a separate component for reuse
  const renderDirectorForm = () => (
    <Stack spacing={3}>
      {/* Basic Information */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <LimitedTextField
            name="director_name"
            label="Name"
            value={formData.director_name}
            onChange={(e) => handleFieldChange(e, "formData")}
            maxLength={formData.director_name_maxlenth}
            error={formData.director_name_error}
            errorText="Please enter Name!"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <LimitedTextField
            name="director_specialization"
            label="Specialization"
            value={formData.director_specialization}
            onChange={(e) => handleFieldChange(e, "formData")}
            maxLength={formData.director_specialization_maxlenth}
            error={formData.director_specialization_error}
            errorText="Please enter Specialization!"
          />
        </Grid>
      </Grid>

      {/* Description */}
      <LimitedTextField
        name="director_short_desc"
        label="Description"
        value={formData.director_short_desc}
        onChange={(e) => handleFieldChange(e, "formData")}
        maxLength={formData.director_short_desc_maxlenth}
        error={formData.director_short_desc_error}
        errorText="Please enter Description!"
        multiline
        rows={3}
      />

      {/* Social Links */}
      <Typography variant="h6">Social Links</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <LimitedTextField
            name="director_facebook_link"
            label="Facebook Link"
            value={formData.director_facebook_link}
            onChange={(e) => handleFieldChange(e, "formData")}
            error={formData.director_facebook_link_error}
            errorText="Please enter valid Facebook link"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <LimitedTextField
            name="director_twitter_link"
            label="Twitter Link"
            value={formData.director_twitter_link}
            onChange={(e) => handleFieldChange(e, "formData")}
            error={formData.director_twitter_link_error}
            errorText="Please enter valid Twitter link"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <LimitedTextField
            name="director_instagram_link"
            label="Instagram Link"
            value={formData.director_instagram_link}
            onChange={(e) => handleFieldChange(e, "formData")}
            error={formData.director_instagram_link_error}
            errorText="Please enter valid Instagram link"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <LimitedTextField
            name="director_pinterest_link"
            label="Pinterest Link"
            value={formData.director_pinterest_link}
            onChange={(e) => handleFieldChange(e, "formData")}
            error={formData.director_pinterest_link_error}
            errorText="Please enter valid Pinterest link"
          />
        </Grid>
      </Grid>

      {/* Image Upload Section */}
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FileUploadArea
            title="Profile Image"
            description={
              <>
                Best image Resolution: 416px X 357px
                <br />
                Supported files types: JPG, JPEG & PNG
              </>
            }
            previewUrl={deskImage}
            fileName={formData.director_image_name}
            onChange={handleDeskImageChange}
            deleteRequest={{
              resource: "director",
              id: formData.director_id,
              field: "director_image",
            }}
            onRemove={() => clearUpload("director_image", "deskImage")}
            accept=".png,.jpg,.jpeg"
            error={formData.director_image_error}
            errorText="Please upload an image!"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FileUploadArea
            title="Mobile Profile Image"
            description={
              <>
                Best image Resolution: 300px X 300px
                <br />
                Supported files types: JPG, JPEG & PNG
              </>
            }
            previewUrl={mobImage}
            fileName={formData.director_image_mob_name}
            onChange={handleMobImageChange}
            deleteRequest={{
              resource: "director",
              id: formData.director_id,
              field: "director_image_mob",
            }}
            onRemove={() =>
              clearUpload("director_image_mob", "mobImage")
            }
            accept=".png,.jpg,.jpeg"
            error={formData.director_image_mob_error}
            errorText="Please upload an image!"
          />
        </Grid>
      </Grid>

      {/* Form Actions */}
      <FormActionBar
        onSubmit={submitDirector}
        onReset={resetForm}
        isSubmitting={isLoading}
        isEdit={formData.director_id !== 0}
        successMessage={successMsg}
        errorMessage={errorMsg}
        submitLabel={formData.director_id !== 0 ? "Update" : "Save"}
        resetLabel="Reset"
      />
    </Stack>
  );

  return (
    <ToolBody>
      <Paper elevation={2} sx={{ p: 3 }}>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <TabList
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                setSuccessMsg("");
                setErrorMsg("");
              }}
            >
              <Tab label="Directors Introduction" value="1" />
              <Tab
                label={`${formData.director_id !== 0 ? "Update" : "Add"} Director`}
                value="2"
              />
              <Tab label="Manage Directors" value="3" />
            </TabList>
          </Box>

          {/* TAB 1: Introduction */}
          <TabPanel value="1">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <LimitedTextField
                  name="section_title"
                  label="Title"
                  value={sectionData.section_title}
                  onChange={(e) => handleFieldChange(e, "sectionData")}
                  maxLength={sectionData.section_title_maxlenth}
                  error={sectionData.section_title_error}
                  errorText="Please enter Title!"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <LimitedTextField
                  name="section_description"
                  label="Description"
                  value={sectionData.section_description}
                  onChange={(e) => handleFieldChange(e, "sectionData")}
                  maxLength={sectionData.section_description_maxlenth}
                  error={sectionData.section_description_error}
                  errorText="Please enter Description!"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <FormActionBar
              onSubmit={submitSection}
              isSubmitting={isLoading}
              isEdit={sectionData.director_section_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </TabPanel>

          {/* TAB 2: Add/Edit Director */}
          <TabPanel value="2">{renderDirectorForm()}</TabPanel>

          {/* TAB 3: Manage Directors */}
          <TabPanel value="3">
            <SearchControls
              searchText={tableData.search.text}
              searchType={tableData.search.type}
              searchOptions={searchOptions}
              onSearchChange={handleSearchTextChange}
              onTypeChange={handleSearchTypeChange}
              onSubmit={handleSearch}
              onClear={clearSearch}
              perPage={tableData.pagination.perPage}
              totalCount={tableData.pagination.totalCount}
              onPerPageChange={handlePerPageChange}
            />

            <DataTable
              columns={tableColumns}
              data={tableData.data}
              loading={tableData.loading}
              sortConfig={tableData.sort}
              onSort={handleSort}
              keyField="director_id"
            />

            {tableData.pagination.totalPage > 1 && (
              <TablePagination
                page={tableData.pagination.pageNo}
                totalPages={tableData.pagination.totalPage}
                loading={tableData.loading}
                onChange={handlePageChange}
                entriesPerPage={tableData.pagination.perPage}
                onEntriesChange={handlePerPageChange}
              />
            )}
          </TabPanel>
        </TabContext>
      </Paper>

      {/* Edit Dialog */}
      <BootstrapDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, item: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Director
          <IconButton
            aria-label="close"
            onClick={() => setEditDialog({ open: false, item: null })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderDirectorForm()}</DialogContent>
      </BootstrapDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteModal
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null })}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        loading={isLoading}
      >
        Are you sure you want to delete this director?
      </DeleteModal>
    </ToolBody>
  );
};

export default Directors;
